import { CALCULATOR, MERCH_CARD } from 'pricing-pages-lib/constants/PurchaseMotionsLocations';
import { isHubSpotBETPortal } from 'pricing-pages-lib/utils/portalUtils';
import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import { CRM_SUITE_PRODUCTS } from 'pricing-pages-lib/utils/productsUtils';
import { ENTERPRISE, STARTER } from 'self-service-api/constants/ProductLevels';
import { ANNUAL } from 'self-service-api/constants/Terms';
import { BUY_ADDITIONAL_CONTACTS, BUY_ADDITIONAL_SEATS, BUY_TOUCHLESS, CALCULATE_CONTACTS, CALCULATE_PRICE, CALCULATE_SEATS, TALK_TO_SALES, TALK_TO_SALES_FALLBACK } from 'ui-addon-upgrades/_core/purchaseMotions/PurchaseMotionTypes';
import { FetchPurchaseMotionRequestTypes } from '../actions/PurchaseMotionActions';
import { isUngatedForSeatsSelector } from './GatingSelectors';
import { hasPurchaseMotionsSucceededSelector } from './LoadingStateSelectors';
import { hasBetterExistingDiscountForProductConfigurationsSelector } from './OfferSelectors';
import { crmSuiteOwnershipLevel } from './ProductInventorySelectors';
import { isQuantityUpgradeSelector, productForSkuIdSelector } from './ProductsSelectors';
import { makeRequestStatusSelector } from './RequestStatusSelectors';
import { isDevPortalSelector } from './ScopeSelectors';
import { canUpgradeToProductLevelSuiteSelector } from './UpgradeSelectors';
export const fetchPurchaseMotionStatus = makeRequestStatusSelector(FetchPurchaseMotionRequestTypes.REQUEST);

const allPurchaseMotionsSelector = state => {
  return state.purchaseMotionsByLocation;
};

export const purchaseMotionsSelector = (state, {
  location = MERCH_CARD,
  skuId
}) => {
  const {
    apiName
  } = productForSkuIdSelector(state, {
    skuId
  });
  const allPurchaseMotions = allPurchaseMotionsSelector(state);
  let purchaseMotions = apiName && allPurchaseMotions && allPurchaseMotions[location] && allPurchaseMotions[location].purchaseMotions[apiName];
  if (!purchaseMotions) purchaseMotions = [];

  if (isDevPortalSelector(state)) {
    purchaseMotions = purchaseMotions.filter(({
      key
    }) => key !== BUY_TOUCHLESS);
  }

  return purchaseMotions.map(({
    key
  }) => key);
};
export const talkToSalesTypeSelector = state => {
  const allPurchaseMotions = allPurchaseMotionsSelector(state);

  if (allPurchaseMotions) {
    for (const location of Object.keys(allPurchaseMotions)) {
      for (const apiName of Object.keys(allPurchaseMotions[location].purchaseMotions)) {
        const purchaseMotionsObjectArray = allPurchaseMotions[location].purchaseMotions[apiName];
        const purchaseMotions = purchaseMotionsObjectArray.map(({
          key
        }) => key);
        if (purchaseMotions.includes(TALK_TO_SALES)) return TALK_TO_SALES;
        if (purchaseMotions.includes(TALK_TO_SALES_FALLBACK)) return TALK_TO_SALES_FALLBACK;
      }
    }
  }

  return TALK_TO_SALES_FALLBACK;
};

const hasNoPurchaseMotionsSelector = state => {
  const purchaseMotions = allPurchaseMotionsSelector(state);
  const hasPurchaseMotionsSucceeded = hasPurchaseMotionsSucceededSelector(state);
  return hasPurchaseMotionsSucceeded && Object.keys(purchaseMotions).length === 0;
};

const TOUCHLESS_PURCHASE_MOTION_VARIANTS = [BUY_TOUCHLESS, BUY_ADDITIONAL_SEATS, BUY_ADDITIONAL_CONTACTS, CALCULATE_PRICE, CALCULATE_SEATS, CALCULATE_CONTACTS];
export const purchaseMotionsContainTouchlessVariant = purchaseMotions => {
  return purchaseMotions.some(purchaseMotion => TOUCHLESS_PURCHASE_MOTION_VARIANTS.includes(purchaseMotion));
};
export const skuIdsContainTouchlessPurchaseMotionVariants = (state, {
  skuIds,
  location
}) => {
  return skuIds.every(skuId => {
    const purchaseMotions = purchaseMotionsSelector(state, {
      skuId,
      location
    });
    return purchaseMotionsContainTouchlessVariant(purchaseMotions);
  });
}; // We can send new purchases and quantity upgrades together to UpgradeCatalog
// as long as we're not overwriting a better existing discount for anything
// already owned

export const skuIdsAreUpgradeableMixedTouchlessPurchase = (state, {
  skuIds,
  location = CALCULATOR,
  queryParamTerm
}) => {
  const allTouchlessVariants = skuIdsContainTouchlessPurchaseMotionVariants(state, {
    skuIds,
    location
  });
  if (!allTouchlessVariants) return false;
  const productConfigurations = skuIds.map(skuId => createProductConfig({
    skuId
  }));
  const hasAnyBetterExistingDiscount = hasBetterExistingDiscountForProductConfigurationsSelector(state, {
    productConfigurations,
    queryParamTerm
  });
  return !hasAnyBetterExistingDiscount;
}; // We can send quantity upgrades that don't have matching purchase motions
// (buyAdditionalSeats and buyAdditionalContacts) together to Shopping

export const skuIdsAreQuantityUpgradeMixedTouchlessPurchase = (state, {
  skuIds,
  location = CALCULATOR
}) => {
  const allTouchlessVariants = skuIdsContainTouchlessPurchaseMotionVariants(state, {
    skuIds,
    location
  });
  if (!allTouchlessVariants) return false;
  const productConfigurations = skuIds.map(skuId => createProductConfig({
    skuId
  }));
  return isQuantityUpgradeSelector(state, {
    productConfigurations
  });
};
export const canSkuIdsBePurchasedTouchlesslyTogether = (state, {
  skuIds,
  location = CALCULATOR,
  queryParamTerm
}) => {
  const isUpgradeable = skuIdsAreUpgradeableMixedTouchlessPurchase(state, {
    skuIds,
    location,
    queryParamTerm
  });
  const isQuantityUpgrade = skuIdsAreQuantityUpgradeMixedTouchlessPurchase(state, {
    skuIds,
    location
  });
  return isUpgradeable || isQuantityUpgrade;
};
export const commonPurchaseMotionsSelector = (state, {
  skuIds,
  location,
  queryParamTerm = ANNUAL
}) => {
  const foundPurchaseMotions = {};
  skuIds.forEach(skuId => purchaseMotionsSelector(state, {
    location,
    skuId
  }).forEach(purchaseMotion => {
    if (purchaseMotion in foundPurchaseMotions) {
      foundPurchaseMotions[purchaseMotion]++;
    } else {
      foundPurchaseMotions[purchaseMotion] = 1;
    }
  }));
  let commonPurchaseMotions = Object.keys(foundPurchaseMotions).filter(purchaseMotion => foundPurchaseMotions[purchaseMotion] === skuIds.length);
  const hasNoPurchaseMotions = hasNoPurchaseMotionsSelector(state);
  const isMixedTouchlessPurchase = canSkuIdsBePurchasedTouchlesslyTogether(state, {
    skuIds,
    location,
    queryParamTerm
  });

  if (isMixedTouchlessPurchase && !purchaseMotionsContainTouchlessVariant(commonPurchaseMotions)) {
    commonPurchaseMotions = [BUY_TOUCHLESS, ...commonPurchaseMotions];
  }

  if (!commonPurchaseMotions.length && !hasNoPurchaseMotions) {
    return [talkToSalesTypeSelector(state)];
  }

  return commonPurchaseMotions;
};
export const suitePurchaseMotionsMapSelector = state => {
  const hasNoPurchaseMotions = hasNoPurchaseMotionsSelector(state);

  if (hasNoPurchaseMotions) {
    return {};
  }

  const isUngatedForSeats = isUngatedForSeatsSelector(state);
  const ownedSuiteLevel = crmSuiteOwnershipLevel(state);
  return Object.keys(CRM_SUITE_PRODUCTS).reduce((acc, productLevel) => {
    if (isUngatedForSeats && productLevel === ownedSuiteLevel) {
      return Object.assign({}, acc, {
        [ownedSuiteLevel]: productLevel === ENTERPRISE ? [BUY_ADDITIONAL_SEATS] : [BUY_ADDITIONAL_CONTACTS, BUY_ADDITIONAL_SEATS]
      });
    }

    const canUpgradeToProductLevelSuite = canUpgradeToProductLevelSuiteSelector(state, {
      productLevel
    });
    const ttsType = talkToSalesTypeSelector(state);

    if (!canUpgradeToProductLevelSuite || productLevel !== STARTER) {
      return Object.assign({}, acc, {
        [productLevel]: [ttsType]
      });
    }

    return Object.assign({}, acc, {
      [productLevel]: [BUY_TOUCHLESS, ttsType]
    });
  }, {});
};
export const isRussianBlockedSelector = state => {
  const hasNoPurchaseMotions = hasNoPurchaseMotionsSelector(state);
  const isHSPortal = isHubSpotBETPortal();
  return hasNoPurchaseMotions && !isHSPortal;
};